import { ApiError } from '@/lib/api-client';
import type { LineQuantityPayload, StockShortfall } from './types';

// Pure helpers behind the sales-order line tables and the deliver / invoice
// forms. Kept free of React so the admin vitest (node env) can cover them.

export type LineFulfilmentStatus = 'PENDING' | 'PARTIAL' | 'DELIVERED' | 'COMPLETE';
export type LineQuantityMode = 'deliver' | 'invoice';

export interface LineQuantities {
  quantity: number | string;
  delivered_quantity: number | string;
  invoiced_quantity: number | string;
}

export interface LineFulfilment {
  ordered: number;
  delivered: number;
  invoiced: number;
  remaining_to_deliver: number;
  remaining_to_invoice: number;
  /** Delivered units that have not been invoiced yet (never negative). */
  uninvoiced_delivered: number;
  status: LineFulfilmentStatus;
}

export const FULFILMENT_LABELS: Record<LineFulfilmentStatus, string> = {
  PENDING: 'Pending',
  PARTIAL: 'Partially delivered',
  DELIVERED: 'Delivered',
  COMPLETE: 'Complete',
};

export function toQty(v: string | number | null | undefined): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function formatQty(v: string | number): string {
  return toQty(v).toFixed(3).replace(/\.?0+$/, '');
}

export function lineFulfilment(item: LineQuantities): LineFulfilment {
  const ordered = toQty(item.quantity);
  const delivered = toQty(item.delivered_quantity);
  const invoiced = toQty(item.invoiced_quantity);
  const status: LineFulfilmentStatus =
    delivered >= ordered && invoiced >= ordered ? 'COMPLETE'
      : delivered >= ordered ? 'DELIVERED'
        : delivered > 0 ? 'PARTIAL'
          : 'PENDING';
  return {
    ordered,
    delivered,
    invoiced,
    remaining_to_deliver: Math.max(0, ordered - delivered),
    remaining_to_invoice: Math.max(0, ordered - invoiced),
    uninvoiced_delivered: Math.max(0, Math.min(delivered, ordered) - invoiced),
    status,
  };
}

export function lineMax(item: LineQuantities, mode: LineQuantityMode): number {
  const f = lineFulfilment(item);
  return mode === 'deliver' ? f.remaining_to_deliver : f.remaining_to_invoice;
}

/** so_item_id → quantity text for every line that still has something left in `mode`. */
export function presetAllRemaining<T extends LineQuantities & { id: string }>(items: T[], mode: LineQuantityMode): Record<string, string> {
  const out: Record<string, string> = {};
  for (const it of items) {
    const max = lineMax(it, mode);
    if (max > 0) out[it.id] = formatQty(max);
  }
  return out;
}

/** so_item_id → delivered-but-not-yet-invoiced quantity (invoice mode preset). */
export function presetDeliveredNotInvoiced<T extends LineQuantities & { id: string }>(items: T[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const it of items) {
    const n = lineFulfilment(it).uninvoiced_delivered;
    if (n > 0) out[it.id] = formatQty(n);
  }
  return out;
}

/** Whole units only — the API validates `@IsInt() @Min(1)`. Blank / 0 means "skip this line". */
export function lineQtyError(entered: string, max: number): string | null {
  const t = entered.trim();
  if (t === '' || t === '0') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return 'Enter a valid quantity';
  if (!Number.isInteger(n)) return 'Whole units only';
  if (n > max) return `Exceeds remaining (${formatQty(max)})`;
  return null;
}

export function buildLinePayload(rows: Record<string, string>): LineQuantityPayload[] {
  const out: LineQuantityPayload[] = [];
  for (const [so_item_id, text] of Object.entries(rows)) {
    const n = Number(text.trim());
    if (Number.isFinite(n) && n > 0) out.push({ so_item_id, quantity: n });
  }
  return out;
}

/** The 409 INSUFFICIENT_STOCK body carries per-line shortfalls; anything else → null. */
export function parseStockShortfalls(err: unknown): StockShortfall[] | null {
  if (!(err instanceof ApiError) || err.statusCode !== 409) return null;
  const list = err.body?.shortfalls;
  return Array.isArray(list) ? (list as StockShortfall[]) : null;
}
